import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ShoppingBag, Truck, Store, ChevronLeft, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { useCart, eur } from "@/context/CartContext";
import { ProductCard } from "@/pages/Shop";

export default function ProductPage() {
  const { slug } = useParams();
  const { add } = useCart();
  const [data, setData] = useState(null);
  const [opts, setOpts] = useState({});
  const [qty, setQty] = useState(1);
  useEffect(() => { api.get("/shop/products").then(({ data }) => setData(data)); setOpts({}); setQty(1); }, [slug]);
  if (!data) return <div className="min-h-[60vh]" />;
  const p = data.products.find((x) => x.slug === slug);
  if (!p) return <Navigate to="/boutique" replace />;
  const optionKeys = Object.keys(p.options);
  const missing = optionKeys.filter((k) => !opts[k]);
  const related = data.products.filter((x) => x.category === p.category && x.slug !== p.slug).slice(0, 4);

  const addToCart = () => {
    if (missing.length) { toast.error(`Choisissez : ${missing.join(", ")}`); return; }
    add(p, opts, qty);
    toast.success(`${p.name} ajouté au panier`, { action: { label: "Voir le panier", onClick: () => (window.location.href = "/boutique/panier") } });
  };

  return (
    <div className="pt-28 lg:pt-36" data-testid="product-page">
      <Seo title={`${p.name} | Boutique Horsepower`} description={p.desc} canonical={`https://www.horsepowergarage.fr/boutique/${p.slug}`} />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <Link to="/boutique" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-neutral-400 hover:text-hp-yellow" data-testid="back-to-shop"><ChevronLeft size={14} /> Boutique</Link>
        <div className="grid lg:grid-cols-2 gap-10 mt-6">
          <div className="hp-card overflow-hidden bg-white aspect-square">
            <img src={p.image} alt={p.name} className="h-full w-full object-contain p-6" />
          </div>
          <div>
            <p className="text-hp-gold text-xs tracking-[0.3em] uppercase font-bold">{data.categories.find((c) => c.slug === p.category)?.name}</p>
            <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mt-3">{p.name}</h1>
            <p className="font-display text-3xl mt-4" data-testid="product-detail-price">{p.available ? eur(p.price) : <span className="text-neutral-500 text-lg">Prix communiqué prochainement</span>}</p>
            <p className="text-neutral-300 mt-5 leading-relaxed">{p.desc}</p>

            {optionKeys.map((k) => (
              <div key={k} className="mt-7">
                <p className="text-[11px] font-bold tracking-widest uppercase text-neutral-400 mb-2">{k} {opts[k] && <span className="text-hp-yellow">— {opts[k]}</span>}</p>
                <div className="flex flex-wrap gap-2">
                  {p.options[k].map((v) => (
                    <button key={v} onClick={() => setOpts((o) => ({ ...o, [k]: v }))} data-testid={`opt-${k}-${v}`.replace(/\s|\//g, "-")}
                      className={`px-3.5 py-2 text-xs font-bold uppercase tracking-wide border transition-colors ${opts[k] === v ? "bg-hp-yellow text-black border-hp-yellow" : "border-white/15 text-neutral-200 hover:border-hp-gold"}`}>{v}</button>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex flex-col sm:flex-row gap-3 mt-9">
              <div className="inline-flex items-center border border-white/15">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-3 hover:text-hp-yellow" data-testid="qty-minus"><Minus size={16} /></button>
                <span className="w-10 text-center font-display text-lg" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty((q) => Math.min(20, q + 1))} className="px-3 py-3 hover:text-hp-yellow" data-testid="qty-plus"><Plus size={16} /></button>
              </div>
              <button onClick={addToCart} disabled={!p.available} className="hp-cta flex-1 px-8 py-4 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="add-to-cart">
                <ShoppingBag size={18} /> {p.available ? "Ajouter au panier" : "Bientôt disponible"}
              </button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mt-8 text-sm">
              <div className="hp-card p-4 flex items-start gap-3"><Store className="text-hp-yellow shrink-0" size={20} /><div><p className="font-bold">Retrait en centre</p><p className="text-neutral-400 text-xs mt-0.5">Gratuit dans les 6 centres Horsepower</p></div></div>
              <div className="hp-card p-4 flex items-start gap-3"><Truck className="text-hp-yellow shrink-0" size={20} /><div><p className="font-bold">Livraison à domicile</p><p className="text-neutral-400 text-xs mt-0.5">{eur(data.shipping_fee)} · offerte dès {eur(data.free_shipping_from)}</p></div></div>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-20">
            <h2 className="font-display font-black uppercase tracking-tight text-2xl sm:text-3xl">Dans la même catégorie</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-8">{related.map((r, i) => <ProductCard key={r.slug} p={r} i={i} />)}</div>
          </div>
        )}
      </section>
    </div>
  );
}
